import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { BookOpen, HelpCircle } from 'lucide-react';

const FloatingDocumentationIcon = ({ isDarkMode = false }) => {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const iconRef = useRef(null);

  // Get saved position from localStorage
  const getSavedPosition = () => {
    try {
      const saved = localStorage.getItem('docIconPosition');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate position is within viewport
        // Convert old format (top/y) to new format (bottom)
        if (parsed.x >= 0) {
          if (parsed.bottom !== undefined && parsed.bottom >= 0) {
            return { x: parsed.x, bottom: parsed.bottom };
          } else if (parsed.y !== undefined && parsed.y >= 0) {
            // Convert old top position to bottom position
            const iconSize = 56;
            const bottom = window.innerHeight - parsed.y - iconSize;
            return { x: parsed.x, bottom: Math.max(0, bottom) };
          }
        }
      }
    } catch (e) {
      // Fallback to default
    }
    // Default position: bottom left, above user name (approximately 100px from bottom)
    return { x: 16, bottom: 100 };
  };

  const [position, setPosition] = useState(getSavedPosition);

  // Save position to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('docIconPosition', JSON.stringify(position));
    } catch (e) {
      // Ignore errors
    }
  }, [position]);

  // Build context-aware documentation URL
  const getDocumentationUrl = () => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    
    // Build base URL
    let docUrl = '/user-guide';
    const docParams = new URLSearchParams();

    // Map routes to documentation sections
    if (path === '/') {
      docParams.set('page', 'dashboard');
    } else if (path === '/settings') {
      docParams.set('page', 'settings');
      // Include tab parameter if present
      const tab = searchParams.get('tab');
      if (tab) {
        docParams.set('tab', tab);
      }
      // Include globalTab if present (for nested settings)
      const globalTab = searchParams.get('globalTab');
      if (globalTab) {
        docParams.set('globalTab', globalTab);
      }
    } else if (path === '/users' || path === '/user-management') {
      docParams.set('page', 'users');
    } else if (path === '/logs') {
      docParams.set('page', 'logs');
    } else if (path === '/report' || path === '/reports') {
      docParams.set('page', 'reports');
    } else if (path === '/profile') {
      docParams.set('page', 'profile');
    } else if (path === '/advanced-player') {
      docParams.set('page', 'player');
    } else {
      // For unknown routes, just link to general documentation
      docParams.set('page', 'general');
    }

    // If there are parameters, append them
    if (docParams.toString()) {
      docUrl += '?' + docParams.toString();
    }

    return docUrl;
  };

  const handleClick = (e) => {
    // Prevent navigation if we're dragging
    if (isDragging) {
      return;
    }
    const docUrl = getDocumentationUrl();
    // Open in new tab
    window.open(docUrl, '_blank', 'noopener,noreferrer');
  };

  // Handle dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        e.preventDefault();
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        
        if (!clientX || !clientY) return;
        
        // Calculate new position
        const newX = clientX - dragOffset.x;
        const newY = clientY - dragOffset.y;
        
        // Keep within viewport bounds
        const iconSize = 56; // width/height of icon
        const maxX = window.innerWidth - iconSize;
        const maxY = window.innerHeight - iconSize;
        
        // Calculate bottom position from top position
        const bottomPos = window.innerHeight - newY - iconSize;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          bottom: Math.max(0, Math.min(bottomPos, window.innerHeight - iconSize))
        });
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      }
    };

    // Add touch events for mobile
    const handleTouchMove = (e) => handleMouseMove(e);
    const handleTouchEnd = () => handleMouseUp();

    if (isDragging) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'move';
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, dragOffset]);

  const handleDragStart = (e) => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    if (!clientX || !clientY) return;
    
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });
    setIsDragging(true);
    
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle responsive positioning
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => {
        // Ensure icon stays within viewport
        const iconSize = 56;
        const maxX = window.innerWidth - iconSize;
        const maxBottom = window.innerHeight - iconSize;
        return {
          x: Math.min(prev.x || 16, maxX),
          bottom: Math.min(prev.bottom || 100, maxBottom)
        };
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Don't show on the documentation page itself
  if (location.pathname === '/user-guide') {
    return null;
  }

  // Hide the help button from the interface
  return null;
};

export default FloatingDocumentationIcon;
